import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Descriptions,
    Tag,
    Typography,
    Divider,
    Spin,
    Skeleton,
    FormInstance
} from 'antd';
import CancelButton from '../common/cancelbutton';
import StoreDetailsForm from './storedetailsform';
import FormButton from '../common/formbutton';
import { LanguageDto } from '../../types/languagetypes';
import { Currency } from '../../types/currencytypes';
import { getStoreDetails } from '../../services/storeserivce';

const { Text } = Typography;

interface StoreDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    storeGuid: string;
    form: FormInstance;
    saveLoading: boolean;
    saveDetails: (values: any) => void;
    title?: React.ReactNode;
    footer?: React.ReactNode | null;
    activeLanguages: LanguageDto[];
    activeCurrencies: Currency[];
    [key: string]: any; // for ...rest props
}

interface Store {
    storeNumber: string;
    guid: string;
    pageversionName?: string;
    storeDisplayName: string;
    buildingName: string;
    buildingAddress1: string;
    buildingAddress2?: string;
    buildingCity: string;
    buildingStateCode: string;
    buildingCounty: string;
    buildingZip: string;

    legalEntityName?: string;
    legalEntityAddress1?: string;
    legalEntityAddress2?: string;
    legalEntityCity?: string;
    legalEntityZip?: string;
    legalEntityCountry?: string;
    legalEntityRegistryCode?: string;
    legalEntityVatNumber?: string;
    displayPhoneNumber?: string;

    hasMTO: boolean;
    hasGrabAndGo: boolean;
    hasMTODriveThru: boolean;
    active: boolean;
}

const StoreDetailsDrawer: React.FC<StoreDetailsDrawerProps> = ({
    open,
    onClose,
    storeGuid,
    form,
    saveLoading,
    saveDetails,
    title,
    footer,
    activeLanguages,
    activeCurrencies,
    ...rest
}) => {
    const [store, setStore] = useState<Store | null>(null);
    const [drawerLoading, setDrawerLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchStore = async () => {
            setDrawerLoading(true);
            try {
                const data = await getStoreDetails(storeGuid);
                setStore(data);
            } catch (error) {
                console.error('Error fetching store details.', error);
            } finally {
                setDrawerLoading(false);
            }
        };

        if (storeGuid) {
            fetchStore();
        }
    }, [ storeGuid]);

    const save = () => {
        saveDetails(form.getFieldsValue());
    };

    const renderValue = (value?: string) =>
        value && value.trim() !== "" ? value : "N/A";

    const defaultFooter = (
        <div style={{ textAlign: 'right' }}>
            <CancelButton onClick={() => onClose()} />
            <FormButton title="Save" onClick={save} />
        </div>
    );

    const storeItems =
        store &&
        [
            { key: 'storeNumber', label: 'Number', children: <Text strong>{renderValue(store.storeNumber)}</Text> },
            { key: 'guid', label: 'Store Guid', children: renderValue(store.guid) },
            { key: 'displayPhoneNumber', label: 'Phone', children: renderValue(store.displayPhoneNumber) },
            { key: 'pageversionName', label: 'Pageversion Name', children: renderValue(store.pageversionName) },
            { key: 'storeDisplayName', label: 'Name', children: renderValue(store.storeDisplayName) },
            { key: 'buildingName', label: 'Building', children: renderValue(store.buildingName) },
            { key: 'buildingAddress1', label: 'Address', children: renderValue(store.buildingAddress1) },
            { key: 'buildingAddress2', label: 'Address 2', children: renderValue(store.buildingAddress2) },
            { key: 'buildingCity', label: 'City', children: renderValue(store.buildingCity) },
            { key: 'buildingStateCode', label: 'State', children: renderValue(store.buildingStateCode) },
            { key: 'buildingCounty', label: 'County', children: renderValue(store.buildingCounty) },
            { key: 'buildingZip', label: 'Zip', children: renderValue(store.buildingZip) },
            {
                key: 'hasMTO',
                label: 'Has MTO?',
                children: (
                    <Tag color={store.hasMTO ? 'default' : 'red'}>
                        {store.hasMTO ? 'Yes' : 'No'}
                    </Tag>
                )
            },
            {
                key: 'hasGrabAndGo',
                label: 'Has Grab & Go?',
                children: (
                    <Tag color={store.hasGrabAndGo ? 'default' : 'red'}>
                        {store.hasGrabAndGo ? 'Yes' : 'No'}
                    </Tag>
                )
            },
            {
                key: 'hasMTODriveThru',
                label: 'Has MTO Drive Thru?',
                children: (
                    <Tag color={store.hasMTODriveThru ? 'default' : 'red'}>
                        {store.hasMTODriveThru ? 'Yes' : 'No'}
                    </Tag>
                )
            },
            {
                key: 'active',
                label: 'Active',
                children: (
                    <Tag color={store.active ? 'default' : 'red'}>
                        {store.active ? 'Yes' : 'No'}
                    </Tag>
                )
            },
            { key: 'legalEntityName', label: 'Legal Name', children: renderValue(store.legalEntityName) },
            { key: 'legalEntityAddress1', label: 'Legal Address 1', children: renderValue(store.legalEntityAddress1) },
            { key: 'legalEntityAddress2', label: 'Legal Address 2', children: renderValue(store.legalEntityAddress2) },
            { key: 'legalEntityCity', label: 'Legal City', children: renderValue(store.legalEntityCity) },
            { key: 'legalEntityZip', label: 'Legal Zip', children: renderValue(store.legalEntityZip) },
            { key: 'legalEntityCountry', label: 'Country', children: renderValue(store.legalEntityCountry) },
            { key: 'legalEntityRegistryCode', label: 'Registry Code', children: renderValue(store.legalEntityRegistryCode) },
            { key: 'legalEntityVatNumber', label: 'VAT Number', children: renderValue(store.legalEntityVatNumber) },
        ];

    return (
        <Drawer
            title={title}
            placement="right"
            width={700}
            onClose={onClose}
            open={open}
            footer={footer === null ? null : footer || defaultFooter}
            styles={{
                header: {
                    color: 'white',
                    backgroundColor: 'var(--color-primary)',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    minHeight: '40px'
                }
            }}
            {...rest}
        >
            {drawerLoading ? (
                <Skeleton active />
            ) : store ? (
                <>
                    <Descriptions
                        column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                        layout="horizontal"
                    >
                        {storeItems && storeItems.map(item => (
                            <Descriptions.Item key={item.key} label={item.label}>
                                {item.children}
                            </Descriptions.Item>
                        ))}
                    </Descriptions>

                    <Divider />

                    <Spin tip="Updating..." spinning={saveLoading}>
                        <StoreDetailsForm
                            form={form}
                            activeLanguages={activeLanguages}
                            activeCurrencies={activeCurrencies}
                        />
                    </Spin>
                </>
            ) : (
                <p>No store data available.</p>
            )}
        </Drawer>
    );
};

export default StoreDetailsDrawer;